import {
  CheckCircle2,
  CreditCard,
  Hash,
  KeyRound,
  Loader2,
  LogOut,
  PlugZap,
  QrCode,
  RotateCw,
  SearchCheck,
  Signal,
  Trash2,
  Webhook,
  XCircle,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { stream } from '../api/client.js';
import {
  billing as billingApi,
  instances as instancesApi,
  webhooks as webhooksApi,
} from '../api/resources.js';
import type { Instance } from '../api/resources.js';
import {
  Banner,
  Card,
  CopyButton,
  Empty,
  ErrorState,
  Loading,
  StatusPill,
} from '../components/ui.js';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog.js';
import { Button } from '../components/ui/button.js';
import { Input, inputClass } from '../components/ui/input.js';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs.js';
import { useToast } from '../components/toast.js';
import { COUNTRY_CODES } from '../data/country-codes.js';
import {
  formatDate,
  formatDateTime,
  formatPhone,
  formatRelative,
  formatTime,
} from '../lib/format.js';
import { useAction, useAsync, useTicker } from '../lib/hooks.js';

/**
 * Detalhe da instância (PRD §26, §27, §95, §124).
 *
 * Duas colunas: à esquerda o que se copia para integrar; à direita a conexão. O QR
 * continua vindo pelo stream (§124: a tela vai de "gerando" a "conectada" sem refresh);
 * o código de pareamento é a alternativa para quem está sem a câmera à mão.
 *
 * Nenhum segredo do engine aparece aqui. A `instance key` aparece (decisão de 15/10) porque
 * identifica a instância no provedor para suporte e rastreio — e não autentica nada. O que
 * autentica a integração é a API Key do cliente (§14), e ela mora em Desenvolvedores.
 */
export function InstanceDetailPage() {
  const { instanceId = '' } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  useTicker(5_000);

  const [live, setLive] = useState<Instance | null>(null);
  const [qr, setQr] = useState<{ code: string; expiresAt: string | null } | null>(null);
  const [pairing, setPairing] = useState<{ code: string; expiresAt: string | null } | null>(null);
  const [ddi, setDdi] = useState('55');
  const [phoneDigits, setPhoneDigits] = useState('');
  const [confirming, setConfirming] = useState<null | 'disconnect' | 'restart' | 'remove'>(null);
  const [tab, setTab] = useState<'timeline' | 'diagnose'>('timeline');

  const detail = useAsync(async () => {
    const [instance, timeline] = await Promise.all([
      instancesApi.get(instanceId),
      instancesApi.timeline(instanceId),
    ]);
    return { instance, timeline: timeline.data };
  }, [instanceId]);

  const instance = live ?? detail.data?.instance;
  const connected = instance?.status === 'CONNECTED';
  // Assinatura e webhook da organização: o que a Mega mostra junto da instância, porque é
  // onde o cliente procura "até quando isso funciona" e "para onde vão os eventos".
  const context = useAsync(() => Promise.all([billingApi.subscription(), webhooksApi.list()]), []);
  const subscription = context.data?.[0] ?? null;
  const activeWebhook =
    context.data?.[1]?.data.find((endpoint) => endpoint.status === 'ACTIVE') ??
    context.data?.[1]?.data[0] ??
    null;
  const pairingInProgress = instance?.status === 'WAITING_QR' || instance?.status === 'CONNECTING';

  useEffect(() => {
    setLive(null);
    setQr(null);
    setPairing(null);
  }, [instanceId]);

  useEffect(() => {
    if (!instanceId || !pairingInProgress) return;

    // Só transmite enquanto há o que observar: uma instância já conectada manteria um
    // poll no provedor à toa.
    const close = stream(`/v1/realtime/instances/${instanceId}`, {
      'instance.status': (data) => setLive(data as unknown as Instance),
      'instance.qrcode': (data) =>
        setQr(data as unknown as { code: string; expiresAt: string | null }),
      'instance.connected': (data) => {
        setLive(data as unknown as Instance);
        setQr(null);
        setPairing(null);
        toast.success('WhatsApp conectado', 'A instância está pronta para enviar mensagens.');
        detail.reload();
      },
    });
    return close;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instanceId, pairingInProgress]);

  /** Um comando ao engine; erro vira toast que some, não alerta preso na tela. */
  const operate = useAction(async (action: 'connect' | 'restart' | 'disconnect' | 'status') => {
    try {
      const updated = await instancesApi[action](instanceId);
      setLive(updated);
      if (action === 'restart' || action === 'disconnect') {
        // Os dois derrubam a sessão: QR e código antigos não valem mais.
        setQr(null);
        setPairing(null);
      }
      detail.reload();
      const feedback: Record<typeof action, string> = {
        connect: 'Pareamento iniciado — o QR Code aparece ao lado.',
        restart: 'Instância reiniciada.',
        disconnect: 'Instância desconectada.',
        status: `Status atualizado: ${updated.status}.`,
      };
      toast.success(feedback[action]);
    } catch (error) {
      toast.failure('Comando não executado', error);
      throw error;
    }
  });

  const requestPairing = useAction(async () => {
    try {
      const result = await instancesApi.pairingCode(instanceId, `${ddi}${phoneDigits}`);
      setPairing(result);
      setQr(null); // um artefato de conexão por vez
      toast.success('Código gerado', 'Digite-o no WhatsApp em até alguns minutos.');
    } catch (error) {
      toast.failure('Não foi possível gerar o código', error);
      throw error;
    }
  });

  const remove = useAction(async () => {
    try {
      await instancesApi.remove(instanceId);
      toast.success('Instância removida');
      navigate('/instances', { replace: true });
    } catch (error) {
      toast.failure('Não foi possível remover', error);
      throw error;
    }
  });

  const diagnosis = useAsync(
    async () => (tab === 'diagnose' ? instancesApi.diagnose(instanceId) : null),
    [tab, instanceId],
  );

  if (detail.loading) return <Loading />;
  if (detail.error) return <ErrorState error={detail.error} onRetry={detail.reload} />;
  if (!detail.data || !instance) return null;

  const busy = operate.pending || requestPairing.pending || remove.pending;

  return (
    <>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="m-0 text-xl font-semibold">{instance.name}</h2>
            <StatusPill status={instance.status} />
          </div>
          <div className="mt-0.5 flex items-center gap-1 text-muted-foreground">
            <span className="mono">{instance.id}</span>
            <CopyButton
              value={instance.id}
              label="Copiar ID da instância"
              variant="ghost"
              size="icon-xs"
            />
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" asChild>
            <Link to="/instances" className="text-foreground no-underline">
              Todas as instâncias
            </Link>
          </Button>
          <Button
            variant="destructive"
            size="sm"
            disabled={busy}
            onClick={() => setConfirming('remove')}
          >
            <Trash2 /> Excluir
          </Button>
        </div>
      </div>

      {instance.status === 'LOGGED_OUT' && (
        <Banner tone="warn">
          O aparelho foi desconectado no WhatsApp. É necessário parear novamente.
        </Banner>
      )}
      {instance.status === 'BLOCKED' && (
        <Banner tone="danger">
          Este número foi bloqueado pelo WhatsApp. Não há ação possível pela Talkium.
        </Banner>
      )}

      <div className="grid gap-5 lg:grid-cols-2">
        {/* Coluna esquerda: dados e integração */}
        <Card title="Dados da instância" description="O que você precisa para integrar.">
          <dl className="m-0 grid gap-3">
            <ReadonlyField label="ID da instância" value={instance.id} mono copy />
            <ReadonlyField
              label="Instance key"
              value={detail.data?.instance.instanceKey ?? '—'}
              mono
              copy
            />
            <ReadonlyField label="Número" value={formatPhone(instance.phoneNumber)} />
            <ReadonlyField label="Perfil no WhatsApp" value={instance.profileName ?? '—'} />
            <ReadonlyField label="Conectada desde" value={formatDateTime(instance.connectedAt)} />
            <ReadonlyField label="Última atividade" value={formatRelative(instance.lastSeenAt)} />
            <ReadonlyField label="Criada em" value={formatDateTime(instance.createdAt)} />
          </dl>

          {subscription && (
            <div className="mt-4 grid gap-2 border-t border-border pt-4 text-sm">
              <p className="m-0 text-xs font-medium tracking-wide text-subtle uppercase">
                Assinatura
              </p>
              <dl className="m-0 grid gap-3">
                <ReadonlyField
                  label="Plano"
                  value={subscription.plan?.name ?? 'Período de teste'}
                />
                <ReadonlyField label="Situação" value={subscriptionPhrase(subscription.status)} />
                <ReadonlyField
                  label={subscription.status === 'TRIALING' ? 'Teste termina em' : 'Renova em'}
                  value={formatDate(
                    subscription.status === 'TRIALING' && subscription.trialEnd
                      ? subscription.trialEnd
                      : subscription.currentPeriodEnd,
                  )}
                />
              </dl>
              <Link to="/billing" className="flex items-center gap-2">
                <CreditCard className="size-4" /> Ver plano e cobrança
              </Link>
            </div>
          )}

          <div className="mt-4 grid gap-2 border-t border-border pt-4 text-sm">
            <p className="m-0 text-xs font-medium tracking-wide text-subtle uppercase">Webhook</p>
            {activeWebhook ? (
              <ReadonlyField label="URL ativa" value={activeWebhook.url} mono copy />
            ) : (
              <p className="m-0 text-muted-foreground">
                Nenhum webhook configurado. Os eventos desta instância ficam só na linha do tempo.
              </p>
            )}
            <Link to="/webhooks" className="flex items-center gap-2">
              <Webhook className="size-4" />{' '}
              {activeWebhook ? 'Gerenciar webhooks' : 'Configurar um webhook'}
            </Link>
          </div>

          <div className="mt-4 grid gap-2 border-t border-border pt-4 text-sm">
            <p className="m-0 text-xs font-medium tracking-wide text-subtle uppercase">
              Integração
            </p>
            <Link to="/developers/keys" className="flex items-center gap-2">
              <KeyRound className="size-4" /> Autentique com uma API Key da organização
            </Link>
            <Link to="/developers/explorer" className="flex items-center gap-2">
              <Signal className="size-4" /> Teste um envio no API Explorer
            </Link>
          </div>
        </Card>

        {/* Coluna direita: conexão */}
        <Card title="Conexão">
          {connected ? (
            <div className="flex flex-col items-center gap-3 py-6 text-center">
              <span className="flex size-14 items-center justify-center rounded-full bg-success-soft">
                <CheckCircle2 className="size-8 text-success" />
              </span>
              <span className="font-semibold text-success">WhatsApp conectado</span>
              <span className="mono text-xs text-muted-foreground">
                {instance.profileName ? `${instance.profileName} · ` : ''}
                {formatPhone(instance.phoneNumber)}
              </span>
              <NumberCheckForm instanceId={instance.id} />
            </div>
          ) : (
            <Tabs defaultValue="qr">
              <p className="mt-0 mb-3 text-center text-sm text-muted-foreground">
                Escaneie o <strong>QR Code</strong> ou digite o <strong>código</strong> no WhatsApp
                em <em>Aparelhos conectados</em>.
              </p>
              <div className="flex justify-center">
                <TabsList>
                  <TabsTrigger value="qr">
                    <QrCode /> QR Code
                  </TabsTrigger>
                  <TabsTrigger value="code">
                    <Hash /> Código
                  </TabsTrigger>
                </TabsList>
              </div>

              <TabsContent value="qr" className="flex flex-col items-center gap-3">
                <div className="flex size-60 items-center justify-center overflow-hidden rounded-xl border border-border bg-muted/40">
                  {qr ? (
                    qr.code.startsWith('data:image') ? (
                      <img
                        src={qr.code}
                        alt="QR Code para parear o WhatsApp"
                        className="size-60 rounded-lg bg-white p-2"
                      />
                    ) : (
                      <pre className="code max-w-full overflow-x-auto p-2 text-xs">{qr.code}</pre>
                    )
                  ) : pairingInProgress ? (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Loader2 className="size-4 animate-spin text-primary" /> Gerando QR Code…
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2 px-4 text-center">
                      <span className="flex size-12 items-center justify-center rounded-full bg-danger-soft">
                        <PlugZap className="size-6 text-danger" />
                      </span>
                      <span className="text-sm font-medium">WhatsApp desconectado</span>
                      <span className="text-xs text-muted-foreground">
                        Clique em "Gerar QR Code" para parear.
                      </span>
                    </div>
                  )}
                </div>
                <span className="subtle">
                  {qr?.expiresAt
                    ? `Expira ${formatRelative(qr.expiresAt)}`
                    : qr
                      ? 'Aguardando leitura'
                      : ''}
                </span>
                <Button disabled={busy} onClick={() => void operate.run('connect')}>
                  {operate.pending ? <Loader2 className="animate-spin" /> : <QrCode />}
                  Gerar QR Code
                </Button>
              </TabsContent>

              <TabsContent value="code" className="flex flex-col items-center gap-3">
                {pairing ? (
                  <div className="flex w-full max-w-xs flex-col items-center gap-3 rounded-xl border border-border bg-muted/40 p-4 text-center">
                    <div className="flex items-center gap-2">
                      <span className="rounded-md bg-terminal px-4 py-2 font-mono text-2xl font-semibold tracking-widest text-terminal-foreground">
                        {pairing.code}
                      </span>
                      <CopyButton value={pairing.code} label="Copiar código" />
                    </div>
                    <p className="m-0 text-xs text-muted-foreground">
                      No WhatsApp: Configurações → Aparelhos conectados → Conectar aparelho →
                      <strong> Conectar com número de telefone</strong>.
                    </p>
                    <p className="m-0 text-xs text-muted-foreground">
                      O código expira em poucos minutos — gere outro se precisar.
                    </p>
                    <Button variant="link" size="sm" onClick={() => setPairing(null)}>
                      Usar outro número
                    </Button>
                  </div>
                ) : (
                  <form
                    className="flex w-full max-w-xs flex-col gap-2"
                    onSubmit={(event) => {
                      event.preventDefault();
                      void requestPairing.run();
                    }}
                  >
                    <label htmlFor="pairing-ddi" className="sr-only">
                      País
                    </label>
                    <select
                      id="pairing-ddi"
                      className={inputClass}
                      value={ddi}
                      onChange={(event) => setDdi(event.target.value)}
                    >
                      {COUNTRY_CODES.map((country) => (
                        <option key={country.ddi} value={country.ddi}>
                          {country.flag} {country.name} +{country.ddi}
                        </option>
                      ))}
                    </select>
                    <label htmlFor="pairing-number" className="sr-only">
                      Número com DDD
                    </label>
                    <Input
                      id="pairing-number"
                      inputMode="numeric"
                      placeholder="11999999999"
                      value={phoneDigits}
                      onChange={(event) => setPhoneDigits(event.target.value.replace(/\D/g, ''))}
                      maxLength={Math.max(1, 15 - ddi.length)}
                      className="font-mono"
                    />
                    <Button type="submit" disabled={busy || phoneDigits.length < 8}>
                      {requestPairing.pending ? <Loader2 className="animate-spin" /> : <Hash />}
                      Gerar código
                    </Button>
                  </form>
                )}
              </TabsContent>
            </Tabs>
          )}

          <div className="mt-4 flex flex-wrap justify-center gap-2 border-t border-border pt-4">
            <Button
              variant="secondary"
              size="sm"
              disabled={busy}
              onClick={() => void operate.run('status')}
            >
              <Signal /> Status
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={busy}
              onClick={() => setConfirming('disconnect')}
            >
              <LogOut /> Desconectar
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={busy}
              onClick={() => setConfirming('restart')}
            >
              <RotateCw /> Reiniciar
            </Button>
          </div>
        </Card>
      </div>

      <Card bodyless>
        <Tabs value={tab} onValueChange={(value) => setTab(value as 'timeline' | 'diagnose')}>
          <div className="border-b border-border px-4 py-2.5">
            <TabsList>
              <TabsTrigger value="timeline">Timeline</TabsTrigger>
              <TabsTrigger value="diagnose">Diagnóstico</TabsTrigger>
            </TabsList>
          </div>
          <TabsContent value="timeline" className="m-0 p-4">
            {detail.data.timeline.length === 0 ? (
              <Empty title="Sem eventos ainda" />
            ) : (
              <ul className="timeline">
                {detail.data.timeline.map((event) => (
                  <li key={event.id}>
                    <time dateTime={event.occurredAt}>{formatTime(event.occurredAt)}</time>
                    <div>
                      <div>
                        {event.type}
                        {event.fromStatus && event.toStatus && (
                          <span className="muted">
                            {' '}
                            — {event.fromStatus} → {event.toStatus}
                          </span>
                        )}
                      </div>
                      {event.message && <div className="subtle">{event.message}</div>}
                      {event.requestId && (
                        <div className="row">
                          <code className="subtle">{event.requestId}</code>
                          <CopyButton
                            value={event.requestId}
                            label="Copiar ID"
                            variant="ghost"
                            size="icon-xs"
                          />
                          <Link
                            className="subtle"
                            to={`/developers/logs?requestId=${event.requestId}`}
                          >
                            Ver requisição
                          </Link>
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </TabsContent>
          <TabsContent value="diagnose" className="m-0 p-4">
            {diagnosis.loading ? (
              <Loading label="Diagnosticando…" />
            ) : diagnosis.data ? (
              <div className="stack">
                {diagnosis.data.checks.map((check) => (
                  <div key={check.component} className="spread">
                    <span>{check.component}</span>
                    <span className={`pill pill-${check.ok ? 'ok' : 'danger'}`}>
                      {check.ok ? '✓ ok' : '✕ falha'}
                      {check.detail ? ` · ${check.detail}` : ''}
                    </span>
                  </div>
                ))}
                <Banner tone={diagnosis.data.recommendation ? 'warn' : 'info'}>
                  <div>
                    <strong>{diagnosis.data.conclusion}</strong>
                    {diagnosis.data.recommendation && <div>{diagnosis.data.recommendation}</div>}
                  </div>
                </Banner>
              </div>
            ) : null}
          </TabsContent>
        </Tabs>
      </Card>

      <AlertDialog open={confirming !== null} onOpenChange={(open) => !open && setConfirming(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirming === 'disconnect' && 'Desconectar a instância?'}
              {confirming === 'restart' && 'Reiniciar a instância?'}
              {confirming === 'remove' && 'Excluir a instância?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirming === 'disconnect' &&
                'A instância será desconectada do WhatsApp e precisará parear de novo.'}
              {confirming === 'restart' &&
                'A sessão será reiniciada no servidor. A conexão pode cair por alguns instantes.'}
              {confirming === 'remove' &&
                'O número é desconectado e a instância deixa de existir. Esta ação não pode ser desfeita.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              destructive={confirming !== 'restart'}
              onClick={() => {
                const action = confirming;
                setConfirming(null);
                if (action === 'remove') void remove.run();
                else if (action) void operate.run(action);
              }}
            >
              Confirmar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function subscriptionPhrase(status: string): string {
  switch (status) {
    case 'TRIALING':
      return 'Período de teste';
    case 'ACTIVE':
      return 'Ativa';
    case 'PAST_DUE':
      return 'Pagamento pendente';
    case 'GRACE_PERIOD':
      return 'Em carência';
    case 'RESTRICTED':
      return 'Restrita';
    case 'SUSPENDED':
      return 'Suspensa';
    case 'CANCELED':
      return 'Cancelada';
    default:
      return status;
  }
}

/**
 * "Esse número tem WhatsApp?" — a pergunta que se faz antes de mandar para um contato novo.
 *
 * O resultado fica só nesta tela, e some ao trocar de número: é sobre um terceiro, e a
 * Talkium não o guarda em lugar nenhum. Consultar em massa arrisca o número, por isso
 * aqui é um por vez, à mão; para lote, a API aplica o mesmo teto dos envios.
 */
function NumberCheckForm({ instanceId }: { instanceId: string }) {
  const toast = useToast();
  const [ddi, setDdi] = useState('55');
  const [digits, setDigits] = useState('');
  const [result, setResult] = useState<{
    phone: string;
    exists: boolean;
    jid: string | null;
  } | null>(null);

  // O número da pergunta em voo. Uma resposta que chega depois de o número ter mudado é
  // sobre outro número, e mostrá-la sob o número novo seria afirmar algo falso.
  const asked = useRef<string | null>(null);

  const check = useAction(async () => {
    const phone = `${ddi}${digits}`;
    asked.current = phone;
    try {
      const answer = await instancesApi.checkNumber(instanceId, phone);
      if (asked.current === phone) setResult(answer);
    } catch (error) {
      toast.failure('Não foi possível verificar o número', error);
      throw error;
    }
  });

  return (
    <form
      className="mt-2 flex w-full max-w-xs flex-col gap-2 border-t border-border pt-4 text-left"
      onSubmit={(event) => {
        event.preventDefault();
        void check.run();
      }}
    >
      <p className="m-0 text-xs font-medium tracking-wide text-subtle uppercase">
        Verificar número
      </p>
      <div className="flex gap-2">
        <label htmlFor="check-ddi" className="sr-only">
          País
        </label>
        <select
          id="check-ddi"
          className={`${inputClass} w-28`}
          value={ddi}
          onChange={(event) => {
            setDdi(event.target.value);
            asked.current = null;
            setResult(null);
          }}
        >
          {COUNTRY_CODES.map((country) => (
            <option key={country.ddi} value={country.ddi}>
              {country.flag} +{country.ddi}
            </option>
          ))}
        </select>
        <label htmlFor="check-number" className="sr-only">
          Número com DDD
        </label>
        <Input
          id="check-number"
          inputMode="numeric"
          placeholder="11999999999"
          value={digits}
          onChange={(event) => {
            setDigits(event.target.value.replace(/\D/g, ''));
            asked.current = null;
            setResult(null);
          }}
          maxLength={Math.max(1, 15 - ddi.length)}
          className="font-mono"
        />
      </div>
      <Button
        type="submit"
        variant="secondary"
        size="sm"
        disabled={check.pending || ddi.length + digits.length < 8}
      >
        {check.pending ? <Loader2 className="animate-spin" /> : <SearchCheck />}
        Verificar
      </Button>
      {result && (
        <p
          role="status"
          className={`m-0 flex items-center gap-2 text-sm ${result.exists ? 'text-success' : 'text-muted-foreground'}`}
        >
          {result.exists ? <CheckCircle2 className="size-4" /> : <XCircle className="size-4" />}
          {result.exists
            ? `${formatPhone(result.phone)} tem WhatsApp.`
            : `${formatPhone(result.phone)} não tem WhatsApp.`}
        </p>
      )}
    </form>
  );
}

function ReadonlyField({
  label,
  value,
  mono,
  copy,
}: {
  label: string;
  value: string;
  mono?: boolean;
  copy?: boolean;
}) {
  return (
    <div>
      <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
      <dd className={`m-0 mt-0.5 flex items-center gap-1 ${mono ? 'mono' : ''}`}>
        <span className="min-w-0 truncate">{value}</span>
        {copy && value !== '—' && (
          <CopyButton
            value={value}
            label={`Copiar ${label.toLowerCase()}`}
            variant="ghost"
            size="icon-xs"
          />
        )}
      </dd>
    </div>
  );
}
